import { isEmpty, startsWith, concat } from 'lodash'
import { publicUrl } from 'utils'
import App from 'templates/App'
import Login from 'components/Login'
import ResetPassword from 'components/ResetPassword'
import ActivateAccount from 'components/ActivateAccount'
import Support from 'components/Support'
import Dashboard from 'components/Dashboard'
import BIDashboard from 'components/bi/BIDashboard'
import Logout from 'components/auth/Logout'
import Profile from 'components/profiles'
import VettingRequest from 'components/booking/vetting'
import BookingRequest from 'components/booking/request'
import Booking from 'components/booking'
import Billboard from 'components/billboard'
import Payment from 'components/payment'
import Talent from 'components/talent'
import Client from 'components/client'
import Manager from 'components/manager'
import Calendar from 'components/calendar'
import Message from 'components/message'
import Notifications from 'components/notifications'
import System from 'components/system'
import EmailPreview from 'components/EmailPreview'
import Importer from 'components/importer'
import VerifyEmail from '../components/VerifyEmail'
import ConfirmAttendance from '../components/ConfirmAttendance'
import NotificationResponse from '../components/NotificationResponse'
import CampaignIndex from '../components/campaign/CampaignIndex'
import CreateCampaign from '../components/campaign/CreateCampaign'
import EditCampaign from '../components/campaign/EditCampaign'
import store from 'vx/store'

// Import User Profile Routes
import { genericProfileRoutes, talentProfileRoutes } from './routes/profile'

const permissionsForInactiveManager = (user, is, to, from) => {
  if (is('manager') && user.status !== 'active') {
    return false
  }

  return true
}

const permissionsForBIDashboard = (user, is, to, from) => {
  let permittedRoles = ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin', 'reporting-admin']
  const isVlast = store.getters['clientConfig/client_config'] && store.getters['clientConfig/client_config'].modules && store.getters['clientConfig/client_config'].modules.vlast_mode
  const showClientBIBudgetPerStateEnabled = store.getters['clientConfig/client_config'] && store.getters['clientConfig/client_config'].modules && store.getters['clientConfig/client_config'].modules.show_client_booking_budget_per_state
  if (is('client') && isVlast && showClientBIBudgetPerStateEnabled) {
    permittedRoles.push('client')
  }
  return permittedRoles
}

export default [
  {
    path: '/',
    component: App,
    redirect: to => {
      return { name: 'dashboard', query: { src: '/' } }
    },
    children: [
      {
        path: '/home',
        component: Dashboard,
        name: 'dashboard',
        meta: {
          redirects: {
            'reporting-admin': 'bidashboards.index'
          }
        }
      },

      /**
       * Client welcome routes
       */
      {
        path: '/bi',
        component: BIDashboard,
        name: 'bidashboards.index',
        meta: {
          permissions: permissionsForBIDashboard
        }
      },
      {
        path: '/email-preview',
        component: EmailPreview,
        name: 'email-preview',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * Talent welcome routes
       */
      {
        path: '/welcome/talent',
        component: Talent.TalentWelcome,
        name: 'talent.welcome',
        meta: {
          permissions: ['admin', 'restricted-admin', 'talent', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * Manager welcome routes
       */
      {
        path: '/welcome/manager',
        component: Manager.ManagerWelcome,
        name: 'manager.welcome',
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * Client welcome routes
       */
      {
        path: '/welcome/:id',
        component: Client.ClientWelcome,
        name: 'client.welcome',
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * Request Vetting Routes
       */
      {
        path: '/request-vetting/:status(vetting|draft|archived)?',
        component: VettingRequest.VettingList,
        name: 'vetting.index',
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/request-vetting/:id',
        component: VettingRequest.VettingSingle,
        name: 'vetting.show',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/request-vetting/:id/edit',
        component: VettingRequest.VettingEdit,
        name: 'vetting.edit',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/request-vetting/:id/duplicate',
        name: 'vetting.duplicate',
        component: VettingRequest.VettingDuplicate,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/campaign/create',
        name: 'campaign.create',
        component: CreateCampaign,
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/campaign/:id/edit',
        name: 'campaign.edit',
        component: EditCampaign,
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/campaigns/:status(active|draft|archived)?',
        name: 'campaigns.index',
        component: CampaignIndex,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/requests/:status(active|archived|event|campaign)?',
        name: 'requests.index',
        component: BookingRequest.RequestList,
        props: true,
        meta: {
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },

      {
        path: '/new-request',
        name: 'requests.create',
        component: BookingRequest.RequestCreate,
        props: true,
        meta: {
          permissions: permissionsForInactiveManager
        }
      },

      /**
       * Booking Request Routes
       */
      {
        path: '/requests/:id/:contact(contact)?',
        alias: '/billboard/:id',
        name: 'requests.show',
        component: BookingRequest.RequestSingle,
        props: true,
        meta: {
          permissions: permissionsForInactiveManager
        },
        beforeEnter: (to, from, next) => {
          if (to.params && to.params.draft) {
            window.location.assign(publicUrl('request/welcome'))
          } else {
            next()
          }
        }
      },
      {
        path: '/requests/:id/edit',
        name: 'requests.edit',
        component: BookingRequest.RequestEdit,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/requests/:id/duplicate',
        name: 'requests.duplicate',
        component: BookingRequest.RequestDuplicate,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * Booking Routes
       */
      {
        path: '/bookings/:status(active|archived|completed-event|completed-campaign|event|campaign|packages|offer)?',
        name: 'bookings.index',
        component: Booking.BookingList,
        props: true,
        meta: {
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/bookings/:id/edit',
        name: 'bookings.edit',
        component: Booking.BookingEdit,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/bookings/:id/duplicate',
        name: 'bookings.duplicate',
        component: Booking.BookingDuplicate,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/bookings/:id',
        component: Booking.BookingSingle,
        props: true,
        children: [
          {
            path: 'overview',
            name: 'bookings.show',
            component: Booking.BookingSingleOverview,
            meta: {
              pace (to, from) {
                // If navigating from any of the other bookings show pages we'll disable pace so we don't
                // get the loader in front of all the content.
                if (startsWith(from.name, 'bookings.show')) {
                  return false
                }

                // For all other requests we'll show the loader, this is primarily for when navigating from
                // the bookings list to a specific booking.
                return true
              }
            }
          },
          {
            path: 'search-details',
            name: 'bookings.show.search',
            component: Booking.BookingSingleSearch,
            meta: {
              pace: false,
              permissions: ['admin', 'restricted-admin', 'client', 'intern', 'sales', 'super-regional-admin', 'regional-admin']
            }
          },
          {
            path: 'payment',
            name: 'bookings.show.payment',
            component: Booking.BookingSinglePayment,
            meta: {
              pace: false,
              permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin']
            }
          },
          {
            path: 'invoice',
            name: 'bookings.show.invoice',
            component: Booking.BookingSingleInvoice,
            meta: {
              pace: false,
              permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin', 'talent', 'manager']
            }
          }
        ]
      },

      /**
       * Payment Routes
       */
      {
        path: '/payments',
        component: Payment.PaymentList,
        name: 'payments.index',
        meta: {
          permissions: ['admin', 'restricted-admin', 'client', 'sales', 'super-regional-admin', 'regional-admin'],
          pace (to, from) {
            return isEmpty(from.matched) || isEmpty(to.query)
          }
        }
      },
      {
        path: '/payments/:bookingId/:action(process|view)/:paymentId',
        name: 'payments.show',
        component: Payment.PaymentSingle,
        props: true
      },
      {
        path: '/payments/:bookingId/receive/:paymentIds',
        name: 'payments.receive',
        component: Payment.PaymentSingleReceived,
        props: true
      },

      /**
       * Billboard Routes
       */
      {
        path: '/billboard',
        name: 'billboard.index',
        component: Billboard.BillboardList,
        meta: {
          modules: ['billboard'],
          pace (to, from) {
            return isEmpty(from.matched) || isEmpty(to.query)
          }
        }
      },

      {
        path: '/profile/:id?/:status(active|archived|completed-event|completed-campaign|event|campaign)?',
        component: Profile.Profile,
        name: 'profile.show',
        props: true,
        params: true,
        children: concat(
          genericProfileRoutes('profile.show'),
          talentProfileRoutes('profile.show')
        ),
        meta: {
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },

      /**
       * Talent Routes
       */
      {
        path: '/talent/:status?',
        component: Talent.TalentList,
        name: 'talent.index',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'intern', 'sales', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query) || from.name === 'talent.index') {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/talent/create',
        component: Talent.TalentCreate,
        name: 'talent.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'intern', 'regional-admin', 'super-regional-admin']
        }
      },
      {
        path: '/talent/:id/profile/:status(active|archived|completed-event|completed-campaign|event|campaign)?',
        component: Profile.Profile,
        name: 'talent.profile.show',
        props: true,
        params: true,
        children: concat(
          genericProfileRoutes('talent.profile.show'),
          talentProfileRoutes('talent.profile.show')
        ),
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'talent', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/talent/:id/view',
        component: Talent.TalentProfile,
        name: 'talent.profile.view',
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'intern', 'client', 'regional-admin', 'super-regional-admin']
        }
      },
      {
        path: '/talent/:id/profile',
        component: Profile.Profile,
        name: 'talent.profile.show',
        props: true,
        params: true,
        children: concat(
          genericProfileRoutes('talent.profile.show'),
          talentProfileRoutes('talent.profile.show')
        ),
        meta: {
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },

      /**
       * Client Routes
       */
      {
        path: '/clients/:status?',
        component: Client.ClientList,
        name: 'clients.index',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'intern', 'sales', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query) || from.name === 'clients.index') {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/clients/:id/profile',
        component: Profile.Profile,
        name: 'clients.profile.show',
        props: true,
        children: genericProfileRoutes('clients.profile.show')
      },
      {
        path: '/clients/:clientId/requests/:status(active|event|campaign|archived|vetting)?',
        name: 'clients.requests.index',
        props: true,
        component: BookingRequest.RequestList,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/clients/:clientId/bookings/:status(active|archived|event|campaign|completed-event|completed-campaign)?',
        name: 'clients.bookings.index',
        component: Booking.BookingList,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'intern', 'sales', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/clients/:clientId/payments',
        name: 'clients.payments.index',
        component: Payment.PaymentList,
        meta: {
          permissions: ['admin', 'restricted-admin', 'intern', 'sales', 'regional-admin', 'super-regional-admin'],
          pace (to, from) {
            return isEmpty(from.matched) || isEmpty(to.query)
          }
        }
      },

      /**
       * Manager Routes
       */
      {
        path: '/managers/:status(all|active|unverified|trash)?',
        component: Manager.ManagerList,
        name: 'managers.index',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'regional-admin', 'super-regional-admin'],
          modules: ['manager'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query) || from.name === 'managers.index') {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/managers/create',
        component: Manager.ManagerCreate,
        name: 'managers.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'regional-admin', 'super-regional-admin'],
          modules: ['manager']
        }
      },
      {
        path: '/clients/create',
        component: Client.ClientCreate,
        name: 'clients.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'regional-admin', 'super-regional-admin']
        }
      },
      {
        path: '/managers/:id/profile',
        component: Profile.Profile,
        name: 'managers.profile.show',
        props: true,
        children: genericProfileRoutes('managers.profile.show'),
        meta: {
          modules: ['manager']
        }
      },
      {
        path: '/managers/:managerId/requests/:status(active|archived|event|campaign)?',
        name: 'managers.requests.index',
        props: true,
        component: BookingRequest.RequestList,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'regional-admin', 'super-regional-admin'],
          modules: ['manager'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/managers/:managerId/bookings/:status(active|archived|event|campaign|completed-event|completed-campaign)?',
        name: 'managers.bookings.index',
        component: Booking.BookingList,
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'regional-admin', 'super-regional-admin'],
          modules: ['manager'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },
      {
        path: '/managers/:managerId/talent/:status(all|active|unverified|featured|trash)?',
        component: Talent.TalentList,
        name: 'managers.talent.index',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin'],
          modules: ['manager'],
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },

      /**
       * Booking Routes
       */
      {
        path: '/threads/:status(active|pinned|archived)?',
        alias: '/inbox/:status(active|pinned|archived)?',
        name: 'threads.index',
        component: Message.ThreadList,
        props: true,
        meta: {
          pace (to, from) {
            if (isEmpty(from.matched)) {
              return true
            } else if (to.params.status !== undefined || !isEmpty(to.query)) {
              return false
            }

            return true
          }
        }
      },

      {
        path: '/notifications',
        component: Notifications.NotificationFeed,
        name: 'notifications.index'
      },

      /**
       * Calendar Routes
       */
      {
        path: '/calendar',
        component: Calendar.CalendarIndex,
        name: 'calendar.index',
        meta: {
          permissions: ['admin', 'restricted-admin', 'manager', 'talent', 'sales', 'client', 'super-regional-admin', 'regional-admin']
        }
      },

      /**
       * System Routes
       */
      {
        path: '/system/settings/create',
        component: System.SystemSettings,
        name: 'system.settings',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      /**
       * System Routes
       */
      {
        path: '/system/opportunities/manage',
        component: System.OpportunityManagement,
        name: 'system.opportunities',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          marketplace: false
        }
      },
      {
        path: '/system/contracts/manage',
        component: System.ContractManagement,
        name: 'system.contracts',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          marketplace: false
        }
      },
      {
        path: '/system/departments/manage',
        component: System.DepartmentManagement,
        name: 'system.departments',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          marketplace: false
        }
      },
      {
        path: '/system/regions/manage',
        component: System.RegionManagement,
        name: 'system.regions',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          marketplace: false
        }
      },
      {
        path: '/system/categories/manage',
        component: System.CategoryManagement,
        name: 'system.categories',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin'],
          marketplace: false
        }
      },
      {
        path: '/system/notifications/create',
        component: System.SystemCustomNotifications,
        name: 'system.notifications.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/notifications/:id',
        component: System.SystemCustomNotificationsShowResponse,
        name: 'system.notifications.show',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/notifications',
        component: System.SystemCustomNotificationsList,
        name: 'system.notifications',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/notifications/create',
        component: System.SystemCustomNotifications,
        name: 'system.notifications.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/notifications/:id',
        component: System.SystemCustomNotificationsShowResponse,
        name: 'system.notifications.show',
        props: true,
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/tag-management',
        component: System.SystemTagManagement,
        name: 'system.tags',
        meta: {
          permissions: ['admin', 'restricted-admin', 'intern', 'sales', 'super-regional-admin', 'regional-admin'],
          pace (to, from) {
            return isEmpty(from.matched) || isEmpty(to.query)
          }
        }
      },
      {
        path: '/system/tag-tree-management',
        component: System.SystemTagTreeManagement,
        name: 'system.tags.tree',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/tag-node-management/:id',
        component: System.SystemTagNodeManagement,
        name: 'system.tags.node',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/tag-management/:id/:tagType',
        props: true,
        component: System.SystemTagManagementSingle,
        name: 'system.tag',
        meta: {
          permissions: ['admin', 'restricted-admin', 'intern', 'sales', 'super-regional-admin', 'regional-admin'],
          pace (to, from) {
            return isEmpty(from.matched) || isEmpty(to.query)
          }
        }
      },
      {
        path: '/system/reports',
        component: System.SystemReports,
        name: 'system.reports',
        meta: {
          permissions: ['admin', 'restricted-admin', 'sales', 'super-regional-admin', 'regional-admin', 'reporting-admin']
        }
      },
      {
        path: '/system/admin-management',
        component: System.SystemAdminManagement,
        name: 'system.admin-management',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        },
        props: true
      },
      {
        path: '/admins/:id/profile',
        component: Profile.Profile,
        name: 'admins.profile.show',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        },
        props: true,
        children: genericProfileRoutes('admins.profile.show')
      },
      {
        path: '/admin/create',
        component: System.SystemAdminManagementAdminCreate,
        name: 'admin.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/system/intern-management',
        component: System.SystemInternManagement,
        name: 'system.intern-management',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        },
        props: true
      },
      {
        path: '/interns/:id/profile',
        component: Profile.Profile,
        name: 'interns.profile.show',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        },
        props: true,
        children: genericProfileRoutes('interns.profile.show')
      },
      {
        path: '/intern/create',
        component: System.SystemInternManagementInternCreate,
        name: 'intern.create',
        meta: {
          permissions: ['admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/video-messages/opt-in',
        component: Talent.TalentPackageOptIn,
        name: 'video-campaign.opt-in',
        meta: {
          permissions: ['talent', 'admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/user-importer',
        component: Importer.UserImporter,
        name: 'importer',
        meta: {
          permissions: ['manager', 'admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      },
      {
        path: '/booking-importer',
        component: Importer.BookingImporter,
        name: 'booking-importer',
        meta: {
          permissions: ['admin']
        }
      },
      {
        path: '/video-kickoffs/opt-in',
        component: Talent.TalentPackageOptIn,
        name: 'kickoffs-with-pickstar.opt-in',
        meta: {
          permissions: ['talent', 'admin', 'restricted-admin', 'super-regional-admin', 'regional-admin']
        }
      }
    ]
  },
  {
    path: '/logout',
    component: Logout,
    name: 'logout'
  },
  {
    path: '/login',
    component: Login,
    name: 'login',
    meta: {
      unauthenticated: true,
      pace (to, from) {
        return false
      }
    }
  },
  {
    path: '/reset-password',
    component: ResetPassword,
    name: 'reset-password',
    meta: {
      unauthenticated: true,
      pace (to, from) {
        return false
      }
    }
  },
  {
    path: '/reset-password/:token',
    component: ResetPassword,
    name: 'reset-password',
    props: true,
    meta: {
      unauthenticated: true,
      pace (to, from) {
        return false
      }
    }
  },
  {
    path: '/activate-account',
    component: ActivateAccount,
    name: 'activate-account',
    meta: {
      unauthenticated: true,
      pace (to, from) {
        return false
      }
    }
  },
  {
    path: '/activate-account/:token',
    component: ActivateAccount,
    name: 'activate-account',
    props: true,
    meta: {
      unauthenticated: true,
      pace (to, from) {
        return false
      }
    }
  },
  {
    path: '/support',
    component: Support,
    name: 'support',
    meta: {
      unauthenticated: true
    }
  },
  {
    path: '/verify-email/:token',
    component: VerifyEmail,
    name: 'verify-email',
    props: true,
    meta: {
      unauthenticated: true
    }
  },
  {
    path: '/confirm-attendance/:token',
    component: ConfirmAttendance,
    name: 'confirm-attendance',
    props: true,
    meta: {
      unauthenticated: true
    }
  },
  {
    path: '/respond/:responseId/:userId/:token',
    component: NotificationResponse,
    name: 'notification-response',
    props: true,
    meta: {
      unauthenticated: true
    }
  }
]
